import { post } from "@src/appV2/api/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { type UploadFileData } from "@src/appV2/lib/Media/types";
import { useMutation, type UseMutationOptions } from "@tanstack/react-query";
import axios, { type AxiosError } from "axios";
import { z } from "zod";

const presignedUploadUrlResponseSchema = z.object({
  data: z.object({
    type: z.literal("presigned-url"),
    id: z.string(),
    attributes: z.object({
      preSignedUrl: z.string(),
      fileStorageRootFolder: z.string(),
      fileStorageFileKey: z.string(),
    }),
  }),
});

interface UploadFileToFileStorageRequest {
  userId: string;
  requirementId: string;
  file: UploadFileData;
}

interface UploadFileToFileStorageResponse {
  preSignedUrl: string;
  fileStorageRootFolder: string;
  fileStorageFileKey: string;
}

export function useUploadFileToFileStorage(
  options: UseMutationOptions<
    UploadFileToFileStorageResponse,
    AxiosError,
    UploadFileToFileStorageRequest
  > = {}
) {
  return useMutation({
    mutationFn: async ({ file, requirementId, userId }) => {
      const { contentType, blob } = file;

      const response = await post({
        url: `${environmentConfig.REACT_APP_DOCUMENTS_SERVICE_REST_API_URL}/documents/presigned-url`,
        data: { userId, requirementId, contentType },
        responseSchema: presignedUploadUrlResponseSchema,
      });

      const { preSignedUrl, fileStorageFileKey, fileStorageRootFolder } =
        response.data.data.attributes;

      await axios.put(preSignedUrl, blob, {
        headers: {
          "Content-Type": contentType,
        },
      });

      return { preSignedUrl, fileStorageFileKey, fileStorageRootFolder };
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.PRESIGNED_URL_UPLOAD_FAILURE,
    },
    ...options,
  });
}
